/* LE PREFERENZE DI CHI GUARDA: formato delle quote, fuso orario e lingua.
 *
 * Sono preferenze di CHI GUARDA, non del sito: il cliente a San Paolo vede le
 * ore sue e le quote come le legge lui, l'operatore in accettazione vede le
 * stesse giocate nel SUO formato e nel SUO fuso. Il server non sa niente di
 * tutto questo: tiene decimali e istanti UTC.
 *
 * DA DOVE VIENE IL VALORE, in ordine:
 *   1. la scelta fatta dal cliente in questa sessione (Impostazioni della
 *      schedina; la tendina della lingua in alto), tenuta nel browser;
 *   2. la skin: `formatoQuote.predefinito` e `fuso` del tenant, scelti alla
 *      creazione della skin (DA COLLEGARE: oggi il tenant non ha quei campi,
 *      la lettura e' gia' scritta e si accende da sola quando arriveranno);
 *   3. per il fuso soltanto, quello del browser: non e' un valore inventato,
 *      e' dove sta davvero chi guarda. Per le quote il decimale, che e' il
 *      valore vero e non un ripiego.
 *
 * LA LINGUA segue la stessa scala: la scelta nella tendina, altrimenti la
 * lingua del browser se e' fra quelle della skin, altrimenti la «prima»
 * della skin. Quali lingue ha la skin lo dice il backoffice (Sito > Skin >
 * Parole della skin), e arriva da by-domain gia' risolto: `lingue` (codice e
 * nome di ognuna, l'italiano sempre in testa) e `linguaPrima`. Se il
 * servizio e' di una versione che non le manda, vale l'elenco
 * LINGUE_DI_PARTENZA qui sotto, scritto in un posto solo. Con la lingua
 * cambiano anche la scrittura delle date (7/10 o 10/7) e dei numeri
 * (1.234,56 o 1,234.56): le fanno formato.js e Intl, con la lingua corrente.
 *
 * NELL'ANTEPRIMA del backoffice (?anteprima=1) comanda la lingua scritta
 * nell'indirizzo (&lingua=en): e' il backoffice che sceglie cosa far vedere.
 *
 * LA LINGUA SCELTA va anche sul conto, se chi guarda e' collegato: con
 * quella partono email, SMS e Telegram (linguaContoApi).
 *
 * COSA NON FA: formato e fuso restano nel browser, non sul conto. E non lascia scegliere un formato che la skin non concede
 * (`formatoQuote.concessi`): quelli restano in elenco, spenti, col motivo.
 */
import {skinCorrente, inAnteprima} from './skin';
import {FORMATI, FORMATO_PREDEFINITO, formatoValido} from './utils/formatoQuote';
import {salvaLinguaSulConto} from './services/linguaContoApi';

const CHIAVE_FORMATO = 'lvc.formatoQuote';
const CHIAVE_FUSO = 'lvc.fuso';
const CHIAVE_LINGUA = 'lvc.lingua';
const CHIAVE_SUONO_EFFETTI = 'lvc.suonoEffetti';

/**
 * Le lingue con cui si parte, finche' la skin non manda le sue: l'italiano
 * e' la lingua in cui le parole sono scritte nel codice (quella di partenza
 * del dizionario), le altre tre sono tradotte. Il nome e' scritto nella
 * lingua stessa, perche' chi la cerca nella tendina la cerca cosi'.
 */
export const LINGUE_DI_PARTENZA = [
  {codice: 'it', nome: 'Italiano'},
  {codice: 'en', nome: 'English'},
  {codice: 'es', nome: 'Español'},
  {codice: 'fr', nome: 'Français'},
];

const ascoltatori = new Set();

function leggi(chiave) {
  try {
    return window.localStorage.getItem(chiave) || '';
  } catch (e) {
    return '';
  }
}

function scrivi(chiave, valore) {
  try {
    if (valore) window.localStorage.setItem(chiave, valore);
    else window.localStorage.removeItem(chiave);
  } catch (e) { /* navigazione privata: vale solo finche' la pagina e' aperta */ }
  ascoltatori.forEach((f) => f());
}

function dallaSkin() {
  const s = skinCorrente() || {};
  const fq = s.formatoQuote || {};
  const lingue = Array.isArray(s.lingue)
    ? s.lingue.filter((l) => l && typeof l.codice === 'string' && l.codice)
    : [];
  return {
    formatoPredefinito: formatoValido(fq.predefinito) ? fq.predefinito : null,
    concessi: Array.isArray(fq.concessi) && fq.concessi.length ? fq.concessi : null,
    fuso: typeof s.fuso === 'string' && s.fuso ? s.fuso : null,
    lingue: lingue.length ? lingue : null,
    linguaPrima: typeof s.linguaPrima === 'string' && s.linguaPrima ? s.linguaPrima : null,
  };
}

/** Il fuso del browser, o UTC se il browser non lo dice. */
/* PERCHE' LE RISPOSTE SI TENGONO DA PARTE. Ogni ora scritta sullo schermo
   passa da fuso(), e fuso() costruiva due Intl.DateTimeFormat solo per
   sapere il fuso del telefono e se quello scelto esiste: con le quote di un
   campionato aperte, decine di volte a ogni tocco. Misurato su un telefono
   lento: un sesto del lavoro del tocco su una quota. Il fuso del telefono non
   cambia mentre la pagina e' aperta, e un nome di fuso valido resta valido. */
let fusoDelTelefono = '';
const fusiProvati = new Map();

export function fusoDelBrowser() {
  if (fusoDelTelefono) return fusoDelTelefono;
  try {
    fusoDelTelefono = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    fusoDelTelefono = 'UTC';
  }
  return fusoDelTelefono;
}

export function fusoValido(fuso) {
  if (!fuso) return false;
  if (fusiProvati.has(fuso)) return fusiProvati.get(fuso);
  let valido;
  try {
    Intl.DateTimeFormat('it', {timeZone: fuso});
    valido = true;
  } catch (e) {
    valido = false;
  }
  fusiProvati.set(fuso, valido);
  return valido;
}

/** I formati che la skin concede, con quelli negati marcati e non tolti. */
export function formatiDisponibili() {
  const {concessi} = dallaSkin();
  return FORMATI.map((f) => ({
    ...f,
    concesso: !concessi || concessi.includes(f.chiave),
  }));
}

export function formatoQuota() {
  const scelto = leggi(CHIAVE_FORMATO);
  const {formatoPredefinito, concessi} = dallaSkin();
  if (formatoValido(scelto) && (!concessi || concessi.includes(scelto))) return scelto;
  return formatoPredefinito || FORMATO_PREDEFINITO;
}

export function impostaFormatoQuota(chiave) {
  if (!formatoValido(chiave)) return;
  const {concessi} = dallaSkin();
  if (concessi && !concessi.includes(chiave)) return;
  scrivi(CHIAVE_FORMATO, chiave);
}

export function fuso() {
  const scelto = leggi(CHIAVE_FUSO);
  if (fusoValido(scelto)) return scelto;
  const {fuso: dellaSkin} = dallaSkin();
  return fusoValido(dellaSkin) ? dellaSkin : fusoDelBrowser();
}

/** Vuoto = torna a quello della skin / del browser. */
export function impostaFuso(valore) {
  if (valore && !fusoValido(valore)) return;
  scrivi(CHIAVE_FUSO, valore || '');
}

/** Le lingue fra cui si sceglie: quelle della skin, o quelle di partenza. */
export function lingueDisponibili() {
  const {lingue} = dallaSkin();
  return lingue || LINGUE_DI_PARTENZA;
}

function codiceValido(codice, lingue) {
  return !!codice && lingue.some((l) => l.codice === codice);
}

/**
 * La lingua del browser, se e' fra quelle disponibili: «fr-FR» vale «fr».
 * Si guardano tutte quelle che il browser elenca, nell'ordine suo.
 */
export function linguaDelBrowser(lingue = lingueDisponibili()) {
  let elenco = [];
  try {
    elenco = Array.isArray(navigator.languages) && navigator.languages.length
      ? navigator.languages : [navigator.language];
  } catch (e) {
    elenco = [];
  }
  for (const voce of elenco) {
    const corta = String(voce || '').toLowerCase().split('-')[0];
    if (codiceValido(corta, lingue)) return corta;
  }
  return '';
}

/** Nell'anteprima del backoffice: la lingua chiesta nell'indirizzo, se c'e'. */
function linguaDellAnteprima() {
  try {
    if (!inAnteprima()) return '';
    return (new URLSearchParams(window.location.search).get('lingua') || '').toLowerCase();
  } catch (e) {
    return '';
  }
}

/** La lingua con cui si scrive il sito adesso. */
export function lingua() {
  const lingue = lingueDisponibili();
  const chiesta = linguaDellAnteprima();
  if (codiceValido(chiesta, lingue)) return chiesta;
  const scelta = leggi(CHIAVE_LINGUA);
  if (codiceValido(scelta, lingue)) return scelta;
  const delBrowser = linguaDelBrowser(lingue);
  if (delBrowser) return delBrowser;
  const {linguaPrima} = dallaSkin();
  if (codiceValido(linguaPrima, lingue)) return linguaPrima;
  return lingue[0].codice;
}

/** Vuoto = torna a quella del browser / della skin. */
export function impostaLingua(codice) {
  if (codice && !codiceValido(codice, lingueDisponibili())) return;
  scrivi(CHIAVE_LINGUA, codice || '');
  salvaLinguaSulConto(lingua());
}

/* LA SPUNTA «ACCETTA CAMBIO QUOTA» della finestra Impostazioni.
 *
 * La finestra e' UNA sola e puo' stare sulla schedina, in testata o in tutte
 * e due (modulo Schedina › rotellina): per questo la scelta sta qui e non
 * dentro la schedina, cosi' chi la tocca da una parte la ritrova dall'altra.
 * Null = il giocatore non l'ha toccata, e vale il PREDEFINITO del profilo di
 * gioco (dal backoffice parte tutto), che la schedina scrive qui appena il
 * profilo arriva. Dura finche' la pagina e' aperta, come prima: chi la
 * toglie la toglie per un motivo, e rimettergliela a ogni schedina sarebbe
 * un dispetto. Non va nel browser: il profilo di domani puo' dire altro. */
let sceltaCambioQuota = null;
let predefinitiSchedinaCorrenti = {cambioQuota: false, includeRiproposta: false, noti: false};

/** La spunta com'e' adesso: la scelta del giocatore, altrimenti il profilo. */
export function cambioQuota() {
  return sceltaCambioQuota === null ? predefinitiSchedinaCorrenti.cambioQuota : sceltaCambioQuota;
}

export function impostaCambioQuota(valore) {
  sceltaCambioQuota = Boolean(valore);
  ascoltatori.forEach((f) => f());
}

/** Quello che il profilo di gioco dice: lo scrive la schedina quando lo legge. */
export function predefinitiSchedina() {
  return predefinitiSchedinaCorrenti;
}

export function impostaPredefinitiSchedina(valori) {
  predefinitiSchedinaCorrenti = {...predefinitiSchedinaCorrenti, ...valori, noti: true};
  ascoltatori.forEach((f) => f());
}

/**
 * Per i componenti: si ridisegnano quando una preferenza cambia. Torna la
 * funzione per smettere di ascoltare.
 */
export function ascolta(f) {
  ascoltatori.add(f);
  return () => ascoltatori.delete(f);
}

/**
 * IL SUONO DEGLI EFFETTI (modulo Effetti: il tintinnio delle monete al
 * versamento e al bonus): acceso finche' chi guarda non lo spegne
 * dall'avviso. Una scelta di chi guarda, nel suo browser; il modulo puo'
 * spegnerlo per tutti, e allora questa non conta.
 */
export function suonoEffetti() {
  return leggi(CHIAVE_SUONO_EFFETTI) !== 'spento';
}

export function impostaSuonoEffetti(acceso) {
  scrivi(CHIAVE_SUONO_EFFETTI, acceso ? '' : 'spento');
}
